import { rgbToCss, solidColor, spectralColor } from './palette';
import { columnLayout } from './spectrum';
import type { WaveformData } from './waveform-data';

/**
 * Waveform drawing, ported from `paint_waveform` in the Rust player's `app.rs`.
 *
 * These parameters only ever reshape the *draw*. The stored analysis stays
 * amplitude-accurate and is never mutated — the same split the desktop player keeps
 * between `WaveformAnalysis` and `WaveformParams`.
 *
 * Layout follows SoundCloud rather than the desktop player: the waveform itself is the
 * scrub surface, mirrored asymmetrically with a dimmed reflection below the centreline.
 */

export type ReductionMode = 'peak' | 'rms';
export type ColorMode = 'spectral' | 'solid';

export type WaveformParams = {
  /** Drawn columns across the width, capped at one per bar slot. */
  bins: number;
  mode: ReductionMode;
  /** Vertical curve per column; below 1 lifts quiet passages. */
  gamma: number;
  /** Overall amplitude multiplier. */
  heightScale: number;
  /** Moving-average radius over columns (0 = off). */
  smoothing: number;
  /** Mirror around the centreline rather than drawing up from the baseline. */
  mirror: boolean;
  colorMode: ColorMode;
};

/** Defaults lifted from `WaveformParams::default()` in `waveform.rs`. */
export const DEFAULT_WAVEFORM_PARAMS: WaveformParams = {
  bins: 1_100,
  mode: 'rms',
  gamma: 0.65,
  heightScale: 1.4,
  smoothing: 1,
  mirror: true,
  colorMode: 'spectral',
};

/**
 * On-screen width of one bar plus its gap. Fixed so a wider viewport shows *more*
 * bars rather than fatter ones — the same reasoning recorded in `spectrum.rs`.
 */
export const BAR_PITCH = 3;

/** Fraction of the upper half's height used by the reflection below the centreline. */
const REFLECTION_SCALE = 0.35;

export function clampParams(params: WaveformParams): WaveformParams {
  return {
    bins: Math.round(Math.min(Math.max(params.bins, 32), 4_000)),
    mode: params.mode,
    gamma: Math.min(Math.max(params.gamma, 0.2), 2),
    heightScale: Math.min(Math.max(params.heightScale, 0.2), 3),
    smoothing: Math.round(Math.min(Math.max(params.smoothing, 0), 8)),
    mirror: params.mirror,
    colorMode: params.colorMode,
  };
}

/** Moving average over columns — the Rust `smooth` helper. */
function smooth(values: Float32Array, radius: number): Float32Array {
  if (radius <= 0) return values;
  const output = new Float32Array(values.length);
  for (let index = 0; index < values.length; index += 1) {
    const from = Math.max(0, index - radius);
    const to = Math.min(values.length - 1, index + radius);
    let sum = 0;
    for (let k = from; k <= to; k += 1) sum += values[k]!;
    output[index] = sum / (to - from + 1);
  }
  return output;
}

export type WaveformColumns = {
  amplitude: Float32Array;
  low: Float32Array;
  mid: Float32Array;
  high: Float32Array;
};

/**
 * Collapse the stored bins into the columns actually drawn. Peak reduces by max and
 * RMS by energy, matching the two `ReductionMode` branches in `paint_waveform`.
 */
export function buildColumns(
  data: WaveformData,
  params: WaveformParams,
  columnCount: number,
): WaveformColumns {
  const source = params.mode === 'peak' ? data.peak : data.rms;
  const binCount = source.length;
  const columns = Math.max(1, Math.min(columnCount, binCount));

  const amplitude = new Float32Array(columns);
  const low = new Float32Array(columns);
  const mid = new Float32Array(columns);
  const high = new Float32Array(columns);

  for (let column = 0; column < columns; column += 1) {
    const start = Math.floor((column * binCount) / columns);
    const end = Math.min(Math.max(Math.floor(((column + 1) * binCount) / columns), start + 1), binCount);

    if (params.mode === 'peak') {
      let peak = 0;
      for (let index = start; index < end; index += 1) {
        if (source[index]! > peak) peak = source[index]!;
      }
      amplitude[column] = peak;
    } else {
      let energy = 0;
      for (let index = start; index < end; index += 1) energy += source[index]! * source[index]!;
      amplitude[column] = Math.sqrt(energy / (end - start));
    }

    let bandLow = 0;
    let bandMid = 0;
    let bandHigh = 0;
    for (let index = start; index < end; index += 1) {
      if (data.low[index]! > bandLow) bandLow = data.low[index]!;
      if (data.mid[index]! > bandMid) bandMid = data.mid[index]!;
      if (data.high[index]! > bandHigh) bandHigh = data.high[index]!;
    }
    low[column] = bandLow;
    mid[column] = bandMid;
    high[column] = bandHigh;
  }

  return {
    amplitude: params.smoothing > 0 ? smooth(amplitude, params.smoothing) : amplitude,
    low,
    mid,
    high,
  };
}

export type WaveformPaintOptions = {
  data: WaveformData | null;
  params: WaveformParams;
  /** 0..1 playback position. */
  progress: number;
  /** 0..1 hover position, or null when the pointer is away. */
  hover?: number | null;
  width: number;
  height: number;
  devicePixelRatio: number;
  /** Colours for the placeholder drawn before the bake lands. */
  placeholderColor: string;
  playheadColor: string;
};

/**
 * Paint one frame. Pure with respect to everything but the canvas: given the same
 * options it always draws the same picture, which is what makes it testable.
 */
export function paintWaveform(
  context: CanvasRenderingContext2D,
  options: WaveformPaintOptions,
): void {
  const { width, height, params, progress } = options;
  context.clearRect(0, 0, width, height);
  if (width <= 0 || height <= 0) return;

  // With a 35% reflection the drawn shape is 1.35x the upper half, so a centreline at
  // 50% would leave the bottom third empty. Sitting it lower fills the box.
  const upperHeight = params.mirror ? height * 0.72 : height;
  const centerY = params.mirror ? upperHeight : height / 2;
  const playedX = width * Math.min(Math.max(progress, 0), 1);
  // Same spreading rule as the analyser: add columns as the canvas widens, and once
  // the stored bins run out widen the bars rather than leaving dead space at the end.
  const { columns: columnCount, slot, barWidth } = columnLayout(width, BAR_PITCH, params.bins);
  if (columnCount === 0) return;

  if (!options.data) {
    // Flat placeholder while the bake is still queued — playback does not wait for it.
    context.fillStyle = options.placeholderColor;
    const barHeight = Math.max(2, height * 0.06);
    for (let column = 0; column < columnCount; column += 1) {
      context.fillRect(column * slot, centerY - barHeight / 2, barWidth, barHeight);
    }
    return;
  }

  const columns = buildColumns(options.data, params, columnCount);
  const hoverX = options.hover === null || options.hover === undefined ? null : width * options.hover;

  for (let column = 0; column < columns.amplitude.length; column += 1) {
    const x = column * slot;
    const shaped = Math.pow(Math.min(Math.max(columns.amplitude[column]!, 0), 1), params.gamma);
    const barHeight = Math.max(1, Math.min(shaped * upperHeight * params.heightScale, upperHeight));
    const played = x + barWidth / 2 <= playedX;

    const color =
      params.colorMode === 'spectral'
        ? spectralColor(columns.low[column]!, columns.mid[column]!, columns.high[column]!, played)
        : solidColor(columns.amplitude[column]!, played);

    context.fillStyle = rgbToCss(color);
    if (params.mirror) {
      // SoundCloud's asymmetric mirror: full body above, dimmed reflection below.
      context.fillRect(x, centerY - barHeight, barWidth, barHeight);
      context.globalAlpha = 0.45;
      context.fillRect(x, centerY, barWidth, barHeight * REFLECTION_SCALE);
      context.globalAlpha = 1;
    } else {
      context.fillRect(x, height - barHeight, barWidth, barHeight);
    }
  }

  if (hoverX !== null) {
    context.fillStyle = 'rgba(255,255,255,0.10)';
    context.fillRect(0, 0, hoverX, height);
  }

  context.fillStyle = options.playheadColor;
  context.fillRect(Math.min(playedX, width - 1), 0, 1, height);
}

/** Map a pointer x within the canvas to a 0..1 position. */
export function positionFromX(x: number, width: number): number {
  if (width <= 0) return 0;
  return Math.min(Math.max(x / width, 0), 1);
}
